import {
  EditionSubType,
  EditionType,
} from '@graphics/gfx-common/dist/constants'
import { PublishingLocationExpandedDTO } from '@graphics/gfx-common/dist/types'
import {
  IPublishedGraphic,
  IPublishedGraphicBaseLocationMedia,
  IPublishedGraphicBaseLocationAWS,
} from '../types/publishedGraphic'
import { IEditionMetadata } from '../types/pack'
import { publishingHostUrl, safeGet } from './http'

export const publishedGraphicsUrl = `${publishingHostUrl}/published-graphics`

export const getPublishedGraphicById = (
  graphicId: string
): Promise<IPublishedGraphic> => safeGet(`${publishedGraphicsUrl}/${graphicId}`)

export const getPublicLocation = (
  graphic: IPublishedGraphic
):
  | IPublishedGraphicBaseLocationAWS
  | IPublishedGraphicBaseLocationMedia
  | undefined =>
  graphic.publishing.locations.find(
    ({ access, hosted }) => hosted && access === 'public'
  )

export const getPublishedSource = (
  graphic: IPublishedGraphic
): string | undefined =>
  isDataStream(graphic)
    ? getDataStreamURI(graphic)
    : isInteractive(graphic) || isImage(graphic)
    ? getPublishedURI(graphic)
    : getPreviewImageURI(graphic)

export const isDataStream = (graphic) =>
  graphic.graphicType === EditionType.DataStreamGraphic

export const isInteractive = (graphic: IPublishedGraphic): boolean =>
  graphic.graphicType === EditionType.GraphicFolder &&
  graphic.graphicSubType === EditionSubType.Interactive

export const isImage = (graphic: IPublishedGraphic): boolean =>
  !!graphic.mimeType && graphic.mimeType.startsWith('image/')

export const getDataStreamURI = (
  graphic: IPublishedGraphic
): string | undefined => graphic.dataStreamGraphic?.dataStreamURI
export const getPublishedURI = (
  graphic: IPublishedGraphic
): string | undefined =>
  (getPublicLocation(graphic) as IPublishedGraphicBaseLocationAWS)?.publishedURI

export const getPreviewImageURI = (
  graphic: IPublishedGraphic
): string | undefined =>
  (getPublicLocation(graphic) as IPublishedGraphicBaseLocationAWS)
    ?.previewImageURI

export const getEditionPrimaryLocation = <
  T extends
    | PublishingLocationExpandedDTO
    | IPublishedGraphicBaseLocationAWS
    | IPublishedGraphicBaseLocationMedia
>(
  locations: T[]
): T => {
  const publishedLocations = locations.filter(
    (l) =>
      l.access === 'public' &&
      l.hosted &&
      (l as IPublishedGraphicBaseLocationAWS).shortURI
  )
  return (
    publishedLocations.find((l) => l.name.startsWith('public-')) ||
    publishedLocations[0]
  )
}

export const getFileFormat = (mimeType: string | undefined): string => {
  if (!mimeType) {
    return ''
  }
  const format = {
    'application/postscript': 'adobeIllustratorV6.0',
    'application/pdf': 'portableDocumentFormat',
    'application/eps': 'adobeIllustratorV6.0',
    'application/msword': 'msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'msword',
    'application/vnd.ms-excel': 'msexcel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      'msexcel',
    'application/vnd.ms-powerpoint': 'mspowerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      'mspowerpoint',
    'application/x-shockwave-flash': 'macromediaFlashFormatFile',
    'image/jpe': 'jpegBaseline',
    'image/jpg': 'jpegBaseline',
    'image/jpeg': 'jpegBaseline',
    'image/gif': 'gif87a',
    'image/bmp': 'bmp',
    'image/png': 'png',
    'text/html': 'HTML5',
  }[mimeType]
  return format ? `fmt:${format}` : ''
}

export const sanitizeXML = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    .replace(/\n/g, '&#xA;')

export const generateLynxJSON = (
  location: PublishingLocationExpandedDTO | IPublishedGraphicBaseLocationAWS,
  edition: IEditionMetadata | IPublishedGraphic,
  editionId: string,
  editionTitle: string,
  editionDescription: string,
  byline?: string
): string => {
  const publishedYear = new Date(location.publishedDate as string).getFullYear()
  const editionType =
    (edition as IEditionMetadata).editionType ||
    (edition as IPublishedGraphic).graphicType
  const mimeType =
    !edition.mimeType && editionType === EditionType.DataStreamGraphic
      ? 'text/html'
      : !edition.mimeType && editionType === EditionType.DataStreamData
      ? 'application/json'
      : edition.mimeType
  const widthAttr = edition.width ? ` width="${edition.width}"` : ''
  const heightAttr = edition.height ? ` height="${edition.height}"` : ''
  const lynxObject = {
    messageType: 'graphics:graphic:1',
    title: editionTitle,
    contentType: mimeType,
    shortUrl: location.shortURI || null,
    url: location.publishedURI || null,
    rsf: `
      <assert qcode="GXID:${editionId}">
        <rightsInfo>
          <copyrightHolder literal="Thomson Reuters"/>
          <copyrightNotice xml:lang="en">(c) Copyright Thomson Reuters ${
            publishedYear || '2019'
          }. Click For Restrictions - http://about.reuters.com/fulllegal.asp</copyrightNotice>
        </rightsInfo>
        <itemClass qcode="ninat:graphic" />
        ${!!editionTitle && `<headline>${sanitizeXML(editionTitle)}</headline>`}
        ${
          !!editionDescription &&
          `<description role="descRole:caption">${sanitizeXML(
            editionDescription
          )}</description>`
        }
        <infoSource literal="Reuters" role="cRole:source"/>
        <creator literal="Reuters"/>
        ${!!byline && `<by>${sanitizeXML(byline)}</by>`}
        <creditline>Reuters</creditline>
        <altId type="idType:GXID" rtr:isOriginal="1">${editionId}</altId>
        <contentSet>
          <remoteContent id="gfx${editionId}${1}" contenttype="${
            mimeType || ''
          }" format="${getFileFormat(
            mimeType
          )}" rendition="rend:baseImage" hascontent="false"${widthAttr}${heightAttr} href="${location.publishedURI}">
            ${
              !!location.shortURI &&
              `<altLoc type="locType:http" role="locRole:shortUrl">${location.shortURI}</altLoc>`
            }
            <remoteContentExtProperty rel="extCptRel:hasUrlScope" qcode="urlScope:public"/>
            <remoteContentExtProperty rel="extCptRel:isHostedOn" qcode="sys:CDN"/>
          </remoteContent>
        </contentSet>
      </assert>
    `
    .replace(/\n[ ]*/gm, '')
    .replace(/&#xA;/g, '\n'),
    qcode: `GXID:${editionId}`,
  }
  return JSON.stringify(lynxObject)
}

export const getPublishedDate = (
  locationOptions: PublishingLocationExpandedDTO[]
): string => {
  const location = locationOptions.find((l) => l.published)
  return (location && location.publishedDate) || ''
}

export const getPublishedGraphicTag = (
  graphicType: EditionType | '',
  graphicSubType: string | null,
  mediaType?: string
): string => {
  switch (graphicType) {
    case EditionType.GraphicFile:
      return mediaType ?? ''
    case EditionType.GraphicFolder:
      return graphicSubType === 'Interactive' ? 'Interactive' : mediaType ?? ''
    case EditionType.DataStreamData:
      return 'DataStream Data'
    case EditionType.DataStreamGraphic:
      return 'DataStream Chart'
    default:
      return ''
  }
}
